import { useState } from "react";
import { observer } from "mobx-react";
import { toast } from "sonner";
import { AutosaveStatusPill } from "@/components/shared/autosave-status-pill";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useTranslation } from "@/i18n";
import { useStore } from "@/stores/store-context";
import type { MyJobRole } from "./use-my-job-role";

const NO_JOB_ROLE = "__none__";

type SaveStatus = {
  /** False until the first save this visit, so a fresh panel shows no status. */
  touched: boolean;
  isSaving: boolean;
  lastSavedIso: string | null;
  hasError: boolean;
};

/**
 * Self-service staffing job-role picker (Phase 4) for the preferences page,
 * PATCHing the "me" endpoint. The membership comes from `useMyJobRole`, called
 * by the page so the intro above can say whether this picker is showing; it
 * renders nothing while `myJobRole.shown` is false.
 *
 * The pick shows at once and reverts if the save fails, so the value on screen
 * is not proof it was stored. A status pill says so: Saving…, a brief Saved, a
 * persistent Save failed, and at rest a screen-reader "Up to date" that only a
 * confirmed write produces (NG-2642).
 */
export const JobRolePreference = observer(function JobRolePreference({
  myJobRole,
}: {
  myJobRole: MyJobRole;
}) {
  const { t } = useTranslation();
  const { workspaceJobRoles } = useStore();
  const { shown, workspaceSlug, jobRoleId, setJobRoleId } = myJobRole;
  const [save, setSave] = useState<SaveStatus>({
    touched: false,
    isSaving: false,
    lastSavedIso: null,
    hasError: false,
  });

  if (!shown || !workspaceSlug) return null;

  const options = workspaceJobRoles.jobRoles.filter(
    (jobRole) => jobRole.status === "active" || jobRole.id === jobRoleId,
  );
  const labelById = new Map(options.map((jobRole) => [jobRole.id, jobRole.name]));
  const current = jobRoleId ?? NO_JOB_ROLE;

  const handleChange = async (value: string | null) => {
    const next = value === NO_JOB_ROLE ? null : (value ?? null);
    const previous = jobRoleId;
    setJobRoleId(next);
    setSave((prev) => ({ ...prev, touched: true, isSaving: true, hasError: false }));
    try {
      await workspaceJobRoles.setMyJobRole(workspaceSlug, next);
      setSave((prev) => ({ ...prev, isSaving: false, lastSavedIso: new Date().toISOString() }));
      toast.success(t("profile_settings.preferences.save_success"));
    } catch (error) {
      console.error("Failed to set own job role", { workspaceSlug, next, error });
      setJobRoleId(previous);
      setSave((prev) => ({ ...prev, isSaving: false, hasError: true }));
      toast.error(t("profile_settings.preferences.save_error"));
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3">
        <Label htmlFor="preferences-job-role">
          {t("profile_settings.preferences.job_role_label")}
        </Label>
        {save.touched ? (
          <AutosaveStatusPill
            isSaving={save.isSaving}
            lastSavedIso={save.lastSavedIso}
            hasError={save.hasError}
            hasUnpublishedChanges={false}
            changedFields={[]}
            className="min-w-0"
            hideAtRest
          />
        ) : null}
      </div>
      <Select
        value={current}
        disabled={save.isSaving}
        onValueChange={(value) => void handleChange(value)}
      >
        <SelectTrigger id="preferences-job-role" className="w-full">
          <SelectValue>
            {(value) =>
              value && value !== NO_JOB_ROLE
                ? (labelById.get(value) ?? "")
                : t("profile_settings.preferences.job_role_none")
            }
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NO_JOB_ROLE}>
            {t("profile_settings.preferences.job_role_none")}
          </SelectItem>
          {options.map((jobRole) => (
            <SelectItem key={jobRole.id} value={jobRole.id}>
              {jobRole.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
});
